import { REQUEST_MESSAGE_TYPES, Request } from 'schemas'
import { UIContractState as Guarantee } from '../../@types/UIContractState'
import { getAmountValue } from 'components/request-action/sections/forms/utils'
import { generateID } from 'utils/generateMessageID'
import { clearGuarantees } from 'redux/slices/all-guarantees'
import { clearAllLegalEntites } from 'redux/slices/legalEntities'
import { clearAllNotifications } from 'redux/slices/notifications'
import { clearAllTermsConditions } from 'redux/slices/terms'
import { clearUserDetails } from 'redux/slices/user'
import { clearAllPurposeTypes } from 'redux/slices/purposeTypes'
import { clearContractPdf } from 'redux/slices/contractPdf'
import { clearContractHistory } from 'redux/slices/contract-history'
import { clearGuaranteeRequest } from 'redux/slices/create-guarantee-request'
import { ContractTime, LegalEntityParty } from '@types'
import { clearAllAdminCreateActions } from 'redux/slices/admin-create-actions'
import { getBeneficiaryDisplayName } from 'utils/trustEntityUtils'

export const constructRows = (data: Guarantee[]) => {
  const rowsToBeFilled: any = []
  data?.forEach((gx, zindex) => {
    if (gx?.contractType === 'TriContract') {
      const { TriContractState: tcs, reference } = gx
      const eventTimeStamp = tcs?.eventTimestamp
      // Adding for now to supress the duplicate key error
      rowsToBeFilled.push({
        key: tcs?.contractIdentifier ?? + zindex,
        type: 'Digital',
        id: tcs?.contractIdentifier,
        reference,
        status: tcs?.stateDisplay,
        applicant: tcs?.applicants?.[0]?.commonName,
        applicantIdentifier: tcs?.applicants?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData,
        beneficiary: getBeneficiaryDisplayName(tcs?.beneficiaries?.[0]),
        beneficiaryIdentifier: tcs?.beneficiaries?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData,
        issuer: tcs?.issuers?.[0]?.commonName,
        issuerIdentifier: tcs?.issuers?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData,
        amount: tcs?.amount ? getAmountValue(tcs?.amount) : 0,
        requestedDate: eventTimeStamp ? new Date(eventTimeStamp?.substring(0, eventTimeStamp?.length - 5)) : null,
        currency: tcs?.amount?.currencyCode,
        commences: tcs?.commences?.date?.DD
          ? `${tcs?.commences?.date?.DD}/${tcs?.commences?.date?.MM}/${tcs?.commences?.date?.YYYY}`
          : 'No Commence Date',
        expiryDate: tcs?.expires?.date?.DD
          ? `${tcs?.expires?.date?.DD}/${tcs?.expires?.date?.MM}/${tcs?.expires?.date?.YYYY}`
          : 'No Expiry Date'
      })
    } else {
      /*@ts-ignore*/
      const { TriContractPaperRecord: tcpr, reference } = gx
      const eventTimeStamp = tcpr?.effectiveAt
      rowsToBeFilled.push({
        key: tcpr?.paperIdentifier ?? + zindex,
        id: tcpr?.paperIdentifier,
        type: 'Paper',
        reference,
        /*@ts-ignore*/
        status: tcpr?.paperData?.stateDisplay,
        applicant: tcpr?.paperData?.applicants?.[0]?.commonName,
        applicantIdentifier: tcpr?.paperData?.applicants?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData,
        beneficiary: getBeneficiaryDisplayName(tcpr?.paperData?.beneficiaries?.[0]),
        beneficiaryIdentifier: tcpr?.paperData?.beneficiaries?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData,
        issuer: tcpr?.paperData?.issuers?.[0]?.commonName,
        issuerIdentifier: tcpr?.paperData?.issuers?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData,
        amount: tcpr?.paperData?.amount ? getAmountValue(tcpr?.paperData?.amount) : 0,
        requestedDate: eventTimeStamp?.calculatedInstant ?? new Date(eventTimeStamp?.calculatedInstant?.toString() || ''), // Need to verify this
        currency: tcpr?.paperData?.amount?.currencyCode,
        commences: tcpr?.paperData?.commences?.date?.DD
          ? `${tcpr?.paperData?.commences?.date?.DD}/${tcpr?.paperData?.commences?.date?.MM}/${tcpr?.paperData?.commences?.date?.YYYY}`
          : 'No Commence Date',
        expiryDate: tcpr?.paperData?.expires?.date?.DD
          ? `${tcpr?.paperData?.expires?.date?.DD}/${tcpr?.paperData?.expires?.date?.MM}/${tcpr?.paperData?.expires?.date?.YYYY}`
          : 'No Expiry Date'
      })
    }
  })
  return rowsToBeFilled.sort((a, b) => (a.requestedDate > b.requestedDate ? -1 : 1))
}

export const constructRequestRows = (data: Request[], setRows) => {
  const rowsToBeFilled: any = []
  data?.forEach((res: any, zindex) => {
    const amount = res?.TriContractState?.payload?.amount?.outstanding
      ? res?.TriContractState?.payload?.amount?.outstanding +
      ' ' +
      res?.TriContractState?.payload?.amount?.currency
      : '0 AUD'
    rowsToBeFilled.push({
      id: res?.TriContractState?.id,
      key: res?.TriContractState?.id + zindex,
      type: res?.TriContractState?.type,
      status: res?.TriContractState?.status,
      createdBy: res?.TriContractState?.enactedBy[0],
      amount: amount,
      requestedDate: new Date(
        res?.TriContractState?.eventTimestamp.substring(
          0 - res?.TriContractState?.eventTimestamp.length - 5
        )
      ),
      expiryDate: new Date(res?.TriContractState?.expiresAt)
    })
  })
  setRows(rowsToBeFilled)
}

export type Stepper = {
  label: string
  completed: boolean
}

export function base64ToPdf(fileName: string, name: string) {
  let base64String = window.sessionStorage.getItem(name) || ''
  // Decode base64 string to binary
  const binaryString = atob(base64String)

  // Create Uint8Array from binary string
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  // Create Blob from Uint8Array
  const blob = new Blob([bytes.buffer], { type: 'application/pdf' })

  // Create URL for the Blob
  const url = URL.createObjectURL(blob)

  // Create an anchor element
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName

  // Programmatically click the anchor element to trigger the download
  anchor.click()

  // Clean up the URL object after download
  URL.revokeObjectURL(url)
}

export function getLabelFromPathItem(pItem) {
  switch (pItem) {
    case 'A':
      return 'Applicant Approval'
    case 'B':
      return 'Beneficiary Approval'
    case 'I':
      return 'Issuer Approval'
    default:
      return ''
  }
}

export async function sendCall(id, onSocketSend) {
  window.sessionStorage.setItem('contractPdf', '')
  const downloadSnapShotPayload: Request = {
    messageId: generateID(),
    messageType: REQUEST_MESSAGE_TYPES.QueryContractDocument,
    QueryContractDocument: { contractIdentifier: id }
  }
  onSocketSend(downloadSnapShotPayload)
}

export async function sendPDFCall(
  documentIdentifier: string,
  roleIdentifier: string,
  onSocketSend
) {
  window.sessionStorage.setItem('pdf', '')
  const documentReq: Request = {
    messageId: generateID(),
    messageType: REQUEST_MESSAGE_TYPES.QueryDocument,
    QueryDocument: {
      roleIdentifier: roleIdentifier,
      documentIdentifier: documentIdentifier
    }
  }

  onSocketSend(documentReq)
}

export const findIssuerDetailsByID = (issuers, idToSearch) => {
  let issuer
  issuers && issuers?.forEach((issu) => {
    if (issu?.issuerLegalEntity?.legalEntityIdentifier === idToSearch) {
      issuer = [issu?.issuerLegalEntity]
    }
  })
  return issuer
}

export const findTermsNameFromTermsID = (issuers, termsIdentifier) => {
  let termFetched
  issuers.forEach((issu) => {
    issu.terms.forEach((term) => {
      if (term.termsIdentifier === termsIdentifier) {
        termFetched = term
      }
    })
  })
  return termFetched
}

export const findTermsDetailsByID = (terms, idToSearch) => {
  let termFetched
  terms &&
    terms.forEach((term) => {
      if (term.termsIdentifier === idToSearch) {
        termFetched = term
      }
    })
  return termFetched
}

export const fetchIssuerDetailsFromHistory = (entry, issus) => {
  const legalIdentity =
    entry?.WorkflowAction?.bffTripartiteAction?.ENRICH_AND_PROCEED?.issuers?.[0]?.legalEntityIdentifier
  const issuerfetched = findIssuerDetailsByID(issus, legalIdentity)

  return {
    issuer: issuerfetched,
    termsIdentifier: findTermsDetailsByID(
      issus?.[0]?.terms,
      entry?.WorkflowAction?.bffTripartiteAction?.ENRICH_AND_PROCEED?.termsIdentifier
        ? entry?.WorkflowAction?.bffTripartiteAction?.ENRICH_AND_PROCEED?.termsIdentifier
        : entry?.WorkflowAction?.bffTripartiteAction?.CREATE?.contractTermsIdentifier
    )
  }
}

export const fetchTermsID = (history, guarantee: Guarantee) => {
  const actionType = history?.WorkflowAction?.bffTripartiteAction?.actionType
  const terms =
    Object.keys(history).includes('WorkflowAction') &&
      Object.keys(history?.WorkflowAction) &&
      Object.keys(history?.WorkflowAction?.bffTripartiteAction).includes(actionType)
      ? {
        id: history.WorkflowAction?.bffTripartiteAction[actionType]?.contractTermsIdentifier
          ? history.WorkflowAction?.bffTripartiteAction[actionType]?.contractTermsIdentifier
          : history.WorkflowAction?.bffTripartiteAction[actionType]?.termsIdentifier,
        termsDisplay: history.WorkflowAction?.bffTripartiteAction[actionType]?.termsDisplay
      }
      : guarantee?.TriContractState &&
        Object.keys(guarantee?.TriContractState).includes('terms') && guarantee?.TriContractState?.terms &&
        Object.keys(guarantee?.TriContractState?.terms).includes('termsDisplay')
        ? {
          id: guarantee?.TriContractState?.terms?.termsIdentifier,
          termsDisplay: guarantee?.TriContractState?.terms?.termsDisplay
        }
        : ''
  return terms
}

export const demandValues = (history, gxRetrieved: Guarantee) => {
  const demandTypes = ['DEMAND_PARTIAL_PAYMENT', 'DEMAND_FULL_PAYMENT']
  let demandData
  const keyItems = history && history?.entries?.length > 0 && Object.keys(history?.entries[0])
  if (keyItems && keyItems?.includes('ContractDisplayItem') && keyItems.includes('ContractEvent')) {
    if (
      history?.entries[0]?.ContractDisplayItem?.contractConsensusResult === 'PENDING' &&
      history?.entries[0]?.ContractEvent?.TriContractState.demandAmount
    ) {
      demandData = {
        state: history?.entries[0]?.ContractEvent?.TriContractState.transitionEvent,
        demandAmount: history?.entries[0]?.ContractEvent?.TriContractState.demandAmount,
        fullAmount: gxRetrieved?.TriContractState?.amount
      }
    }
  } else if (
    keyItems &&
    keyItems?.includes('WorkflowDisplayItem') &&
    keyItems.includes('WorkflowAction')
  ) {
    const workflow = Object.keys(history.entries[0]?.WorkflowAction?.bffTripartiteAction)
    if (
      workflow.includes(demandTypes[0]) &&
      history?.entries[0]?.WorkflowDisplayItem?.workflowConsensusResult === 'PENDING'
    ) {
      demandData = {
        state: history.entries[0]?.WorkflowAction?.bffTripartiteAction.transitionEvent,
        demandAmount:
          history.entries[0]?.WorkflowAction?.bffTripartiteAction['DEMAND_PARTIAL_PAYMENT']
            ?.demandAmount,
        fullAmount: gxRetrieved?.TriContractState?.amount
      }
    } else if (
      workflow.includes(demandTypes[1]) &&
      history?.entries[0]?.WorkflowDisplayItem?.workflowConsensusResult === 'PENDING'
    ) {
      demandData = {
        state: history.entries[0]?.WorkflowAction?.bffTripartiteAction?.actionType,
        demandAmount: gxRetrieved?.TriContractState?.amount,
        fullAmount: gxRetrieved?.TriContractState?.amount
      }
    } else if (gxRetrieved?.TriContractState?.demandAmount) {
      demandData = {
        state: gxRetrieved?.TriContractState?.transitionEvent,
        demandAmount: gxRetrieved?.TriContractState?.demandAmount,
        fullAmount: gxRetrieved?.TriContractState?.amount
      }
    } else {
      demandData = null
    }
  }
  return demandData
}

export const checkWhetherYepExists = (guarantee?: Guarantee) => {
  let whetherPROCEED = false
  guarantee?.businessUnitActionContext?.[0]?.availableActions?.forEach((ava) => {
    if (ava?.bffTripartiteAction?.actionType === 'ENRICH_AND_PROCEED') {
      whetherPROCEED = true
    }
  })
  return whetherPROCEED
}

export const convertGxDetailsToContext = (gx?: Guarantee) => {
  const { applicants, beneficiaries, issuers, expires, purposeData, amount, terms } = gx?.TriContractState ?? {}
  const obj = {
    reference: gx?.reference,
    applicants: [
      {
        commonName: applicants?.[0]?.commonName,
        legalEntityIdentifier: applicants?.[0]?.legalEntityIdentifier,
        legalEntityCodes: [
          {
            legalEntityCodeType: applicants?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeType,
            legalEntityCodeData: applicants?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData
          }
        ]
      }
    ],
    beneficiaries: [
      {
        commonName: beneficiaries?.[0]?.commonName,
        legalEntityIdentifier: beneficiaries?.[0]?.legalEntityIdentifier,
        legalEntityCodes: [
          {
            legalEntityCodeType: beneficiaries?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeType,
            legalEntityCodeData: beneficiaries?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData
          }
        ],
        designatedPointOfContact: beneficiaries?.[0]?.designatedPointOfContact,
        onboarded: beneficiaries?.[0]?.onboarded,
        requestedTrustee: beneficiaries?.[0]?.requestedTrustee,
        legalEntityType: beneficiaries?.[0]?.legalEntityType,
        handlingInstructions: beneficiaries?.[0]?.handlingInstructions
      }
    ],
    issuers: [
      {
        commonName: issuers?.[0]?.commonName,
        legalEntityIdentifier: issuers?.[0]?.legalEntityIdentifier,
        legalEntityCodes: [
          {
            legalEntityCodeType: issuers?.[0].legalEntityCodes?.[0]?.legalEntityCodeType,
            legalEntityCodeData: issuers?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData
          }
        ]
      }
    ],
    amount,
    terms,
    expiryDate: `${expires?.date?.YYYY}/${expires?.date?.MM}/${expires?.date?.DD}`,
    isOpenEnded: expires?.date?.never,
    purpose: {
      purposeDescription: purposeData ? JSON.parse(purposeData)?.purposeDescription : null,
      contract: purposeData ? { ...JSON.parse(purposeData)?.contract } : null,
      property: purposeData ? { ...JSON.parse(purposeData)?.property } : null
    }
  }
  return obj
}

export const convertPaperGxDetailsToContext = (gx?: Guarantee) => {
  const { paperData } = gx?.TriContractPaperRecord ?? {}
  const { applicants, beneficiaries, issuers, expires, purposeData, amount } = paperData ?? {}

  const obj = {
    reference: gx?.reference,
    applicants: [
      {
        commonName: applicants?.[0]?.commonName,
        legalEntityIdentifier: applicants?.[0]?.legalEntityIdentifier,
        legalEntityCodes: [
          {
            legalEntityCodeType: applicants?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeType,
            legalEntityCodeData: applicants?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData
          }
        ]
      }
    ],
    beneficiaries: [
      {
        commonName: beneficiaries?.[0]?.commonName,
        legalEntityIdentifier: beneficiaries?.[0]?.legalEntityIdentifier,
        legalEntityCodes: [
          {
            legalEntityCodeType: beneficiaries?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeType,
            legalEntityCodeData: beneficiaries?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData
          }
        ],
        designatedPointOfContact: beneficiaries?.[0]?.designatedPointOfContact,
        requestedTrustee: beneficiaries?.[0]?.requestedTrustee,
        legalEntityType: beneficiaries?.[0]?.legalEntityType,
        handlingInstructions: beneficiaries?.[0]?.handlingInstructions
      }
    ],
    issuers: [
      {
        commonName: issuers?.[0]?.commonName,
        legalEntityIdentifier: issuers?.[0]?.legalEntityIdentifier,
        legalEntityCodes: [
          {
            legalEntityCodeType: issuers?.[0].legalEntityCodes?.[0]?.legalEntityCodeType,
            legalEntityCodeData: issuers?.[0]?.legalEntityCodes?.[0]?.legalEntityCodeData
          }
        ]
      }
    ],
    amount,
    expires: expires,
    expiryDate: expires?.date?.YYYY && expires?.date?.MM && expires?.date?.DD ? `${expires?.date?.YYYY}/${expires?.date?.MM}/${expires?.date?.DD}` : '',
    isOpenEnded: expires?.date?.never,
    purpose: {
      purposeDescription: purposeData ? JSON.parse(purposeData)?.purposeDescription : null,
      contract: purposeData ? { ...JSON.parse(purposeData)?.contract } : null,
      property: purposeData ? { ...JSON.parse(purposeData)?.property } : null
    }
  }
  return obj
}
export const purposeDataDetermination = (history, type, OptionalNewExpires?: ContractTime) => {
  let purposeData
  const propertyToCheck =
    type === 'purpose' ? 'newPurposeData' : type === 'expiry' ? 'newExpires' : 'newAmount'
  const keyItems = history && history?.entries?.length > 0 && Object.keys(history?.entries[0])
  if (
    keyItems &&
    keyItems?.includes('ContractDisplayItem') &&
    keyItems?.includes('ContractEvent')
  ) {
    if (history?.entries[0]?.ContractEvent?.TriContractState[propertyToCheck]) {
      const value = history?.entries[0]?.ContractEvent?.TriContractState[propertyToCheck]
      if (type === 'purpose' && value) {
        try {
          purposeData = JSON.parse(value)
        } catch (e) {
          purposeData = null
        }
      } else {
        purposeData = value
      }
    }
  } else if (
    keyItems &&
    keyItems?.includes('WorkflowDisplayItem') &&
    keyItems.includes('WorkflowAction')
  ) {
    const workflow = Object.keys(history.entries[0]?.WorkflowAction?.bffTripartiteAction)
    if (workflow.includes('AMEND') && history.entries[0]?.WorkflowAction?.action !== 'CANCEL') {
      const value = history.entries[0]?.WorkflowAction?.bffTripartiteAction?.AMEND[propertyToCheck]
      if (type === 'purpose' && value) {
        try {
          purposeData = JSON.parse(value)
        } catch (e) {
          purposeData = null
        }
      } else {
        purposeData = value
      }
    } else if (workflow.includes('PROCEED')) {
      if (type === 'expiry') {
        purposeData = OptionalNewExpires
      }
    } else {
      purposeData = null
    }
  }
  return purposeData
}

export const convertEnumValuesToUserReadableForm = (pType: string): string => {
  let userReadablePType =
    pType === 'PAYMENTGX'
      ? 'Payment Guarantee'
      : pType === 'PERFORMANCEGX'
        ? 'Performance Guarantee'
        : 'Commercial Lease'
  return userReadablePType
}

export const fetchReference = (history, gxRetrieved: Guarantee) => {
  const actionType = history && history?.WorkflowAction?.bffTripartiteAction?.actionType
  const refFromWorkflow =
    Object.keys(history).includes('WorkflowAction') &&
      Object.keys(history?.WorkflowAction) &&
      Object.keys(history?.WorkflowAction?.bffTripartiteAction).includes(actionType) &&
      Object.keys(history?.WorkflowAction?.bffTripartiteAction[actionType]).includes('reference')
      ? history?.WorkflowAction?.bffTripartiteAction[actionType]?.reference
      : undefined

  const refToReturn = gxRetrieved?.reference ? gxRetrieved?.reference : refFromWorkflow
  return refToReturn !== '' ? refToReturn : null
}

export const checkIRNUpdate = (history) => {
  const refFromWorkflow =
    history &&
      Object.keys(history)?.includes('WorkflowAction') &&
      Object.keys(history?.WorkflowAction) &&
      Object.keys(history?.WorkflowAction?.bffTripartiteAction)?.includes('UPDATE_REFERENCE') &&
      Object.keys(history?.WorkflowAction?.bffTripartiteAction['UPDATE_REFERENCE'])?.includes(
        'reference'
      ) &&
      Object.keys(history?.WorkflowEvent)?.includes('workflowState') &&
      history?.WorkflowEvent?.workflowState === 'PROPOSED'
      ? history?.WorkflowAction?.bffTripartiteAction['UPDATE_REFERENCE']?.reference
      : undefined

  return refFromWorkflow
}

export const deriveIRNLogicToShow = (guarantee: Guarantee) => {
  let showIRNfield = false
  guarantee?.businessUnitActionContext?.[0]?.availableActions?.forEach((action) => {
    if (
      (action?.action === 'EXECUTE' || action?.action === 'PROPOSE') &&
      !guarantee?.TriContractState?.hasOwnProperty('issued') &&
      !guarantee?.hasOwnProperty('reference')
    ) {
      showIRNfield = true
    }
  })
  return showIRNfield
}

// Helper function to check if a beneficiary object has all required contact information
const hasCompleteContactInfo = (beneficiary: LegalEntityParty | null | undefined): boolean => {
  return (
    !!beneficiary?.contactFirstName &&
    !!beneficiary?.contactLastName &&
    !!beneficiary?.phone &&
    !!beneficiary?.email
  );
};

export const extractThePCBInfo = (
  beneficiaryFromGxRetrieved: LegalEntityParty,
  beneficiaryFromRedux: LegalEntityParty
): LegalEntityParty | undefined => {
  // Prioritize beneficiary from gxRetrieved if it has complete contact info
  if (hasCompleteContactInfo(beneficiaryFromGxRetrieved)) {
    return beneficiaryFromGxRetrieved;
  }

  // Otherwise, fall back to beneficiary from Redux if it has complete contact info
  if (hasCompleteContactInfo(beneficiaryFromRedux)) {
    return beneficiaryFromRedux;
  }

  // If neither has complete contact info, return undefined
  return undefined;
};

export const clearAllReduxCaches = (dispatch) => {
  dispatch(clearGuarantees())
  dispatch(clearAllLegalEntites())
  dispatch(clearAllNotifications())
  dispatch(clearAllTermsConditions())
  dispatch(clearUserDetails())
  dispatch(clearAllPurposeTypes())
  dispatch(clearContractPdf())
  dispatch(clearContractHistory())
  dispatch(clearGuaranteeRequest())
  dispatch(clearAllAdminCreateActions())
}